import React, { useEffect, useMemo, useState } from 'react';

import { getBrands } from 'common/api/search/getBrands';
import { withAuth } from 'features/auth/components/withAuth';

import { useSelector } from 'react-redux';
import { FeaturesState } from 'common/features/featuresReducer';

import { BaseLandingPage } from '../base/BaseLandingPage';
import { FavoritesGrid } from './FavoritesGrid';
import {
    searchResultBrandsToBrands,
    Brand,
} from 'common/features/store/duck/search-generic/utils/searchResultBrandsToBrands';
import { useBrandList } from 'common/hooks/useBrandList';
import { Btn } from 'components/btn/Btn';
import { useHistory } from 'react-router-dom';
import { Routes } from 'routes';
import { editFilter } from 'common/utils/shipToCoordinatorUtils';
import { selectIsShipToCoordinatorEnabled } from 'common/features/store/duck/organization/duck';
import { SearchApiQueryParams } from 'common/api/search';
import { useRootSelector } from 'rootStore';
import { selectFavorites } from 'common/features/store/duck/brands/duck';

const favoritesHeroId = 'favorites_hero_id';

const usePopularBrandCards = () => {
    const { data: brands, loadData } = useBrandList<Brand[]>(searchResultBrandsToBrands);
    const [isLoading, setIsLoadingTo] = useState<boolean>(true);

    useEffect(() => {
        const getFavoriteBrands = async (options: Partial<SearchApiQueryParams> = {}) => {
            const result = await getBrands(options);
            if (result.data?.brands && options.top) {
                // limit to based on `top` parameter
                result.data.brands = result.data.brands.splice(0, options.top);
            }
            return result;
        };
        loadData(getFavoriteBrands)({
            top: 4,
            orderby: 'PopularityRank asc',
        }).finally(() => {
            setIsLoadingTo(false);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        isFavoriteSuggestionsLoading: isLoading,
        favoriteSuggestions: brands || [],
    };
};

const BackToCatalog = () => {
    const history = useHistory();

    const goToCatalog = () => {
        history.push(Routes.Brands);
    };

    return (
        <div className="flex justify-center mb-8 mt-10">
            <Btn onClick={goToCatalog}>Browse Our Catalog</Btn>
        </div>
    );
};

const renderers = { footer: BackToCatalog };

export const Favorites = withAuth()(() => {
    const favorites = useRootSelector(selectFavorites);
    const { pageBrands, loading: brandsLoading } = useSelector(
        (state: FeaturesState) => state.store.searchGrid
    );
    const isShipToCoordinatorEnabled = useSelector(selectIsShipToCoordinatorEnabled);
    const favoriteFilter = useMemo(() => {
        return editFilter(
            isShipToCoordinatorEnabled,
            `search.in(Id, '${favorites.join()}')`
        ) as string;
    }, [isShipToCoordinatorEnabled, favorites]);
    const { isFavoriteSuggestionsLoading, favoriteSuggestions } = usePopularBrandCards();

    return (
        <BaseLandingPage
            firebaseConfigKey={favoritesHeroId}
            filter={favoriteFilter}
            renderers={renderers}
        >
            <FavoritesGrid
                items={pageBrands}
                isLoading={isFavoriteSuggestionsLoading || brandsLoading}
                suggestions={favoriteSuggestions}
            />
        </BaseLandingPage>
    );
});

export default Favorites;
